import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPhone, FiMapPin, FiClock, FiFacebook, FiMail } = FiIcons;

const Contact = () => {
  return (
    <>
      <Helmet>
        <title>Contact Us - Zalabya | Location, Hours & Contact Info</title>
        <meta name="description" content="Visit Zalabya in Mississauga! Find our location, hours, phone number and directions. Call (905) 820-7700 or WhatsApp (647) 537-0181 to order." />
      </Helmet>

      <div className="pt-24 pb-12">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h1 className="text-5xl font-bold text-gradient mb-4">Contact Us</h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Get in touch with us for orders, inquiries, or just to say hello!
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-8"
            >
              <div className="card p-8">
                <h2 className="text-2xl font-bold text-yellow-400 mb-6">Get in Touch</h2>
                
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <SafeIcon icon={FiPhone} className="text-2xl text-pink-400 mt-1" />
                    <div>
                      <h3 className="font-bold text-white mb-1">Phone</h3>
                      <p className="text-gray-300">+1 (905) 820-7700</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <SafeIcon icon={FiPhone} className="text-2xl text-pink-400 mt-1" />
                    <div>
                      <h3 className="font-bold text-white mb-1">WhatsApp</h3>
                      <p className="text-gray-300">+1 (647) 537-0181</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <SafeIcon icon={FiMapPin} className="text-2xl text-pink-400 mt-1" />
                    <div>
                      <h3 className="font-bold text-white mb-1">Address</h3>
                      <p className="text-gray-300">
                        3700 Eglinton Ave W unit 56<br />
                        Mississauga, ON L5M 2R9<br />
                        Canada
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <SafeIcon icon={FiClock} className="text-2xl text-pink-400 mt-1" />
                    <div>
                      <h3 className="font-bold text-white mb-1">Hours</h3>
                      <p className="text-gray-300">
                        Open Daily<br />
                        Perfect for late dessert cravings
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <SafeIcon icon={FiFacebook} className="text-2xl text-pink-400 mt-1" />
                    <div>
                      <h3 className="font-bold text-white mb-1">Social Media</h3>
                      <a
                        href="https://www.facebook.com/Zalabya.ca"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-yellow-400 hover:underline"
                      >
                        Follow us on Facebook
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Services */}
              <div className="card p-8">
                <h3 className="text-xl font-bold text-yellow-400 mb-4">Our Services</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="text-3xl mb-2">🏪</div>
                    <p className="text-sm text-gray-300">Dine-in</p>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl mb-2">🥡</div>
                    <p className="text-sm text-gray-300">Takeout</p>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl mb-2">🚚</div>
                    <p className="text-sm text-gray-300">Delivery</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Map */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="card overflow-hidden"
            >
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d723.0669688851461!2d-79.7281651!3d43.538457699999995!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x882b436f86ee45bb%3A0xbb871f1a67f20f5e!2sZalabya!5e0!3m2!1sen!2s!4v1757121973245!5m2!1sen!2s"
                width="100%"
                height="500"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Zalabya Location"
              ></iframe>
            </motion.div>
          </div>

          {/* Quick Actions */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            <div className="card p-8 text-center">
              <SafeIcon icon={FiPhone} className="text-4xl text-pink-400 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-yellow-400 mb-4">Call to Order</h3>
              <p className="text-gray-300 mb-4">Quick phone orders for pickup or delivery</p>
              <a href="tel:+19058207700" className="btn-primary">
                Call Now
              </a>
            </div>

            <div className="card p-8 text-center">
              <SafeIcon icon={FiPhone} className="text-4xl text-pink-400 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-yellow-400 mb-4">WhatsApp</h3>
              <p className="text-gray-300 mb-4">Message us for quick responses</p>
              <a
                href="https://wa.me/16475370181"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
              >
                WhatsApp
              </a>
            </div>

            <div className="card p-8 text-center">
              <SafeIcon icon={FiMapPin} className="text-4xl text-pink-400 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-yellow-400 mb-4">Get Directions</h3>
              <p className="text-gray-300 mb-4">Find us easily with GPS navigation</p>
              <a
                href="https://maps.app.goo.gl/UKjdkNcxDo6BqAQTA"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
              >
                Directions
              </a>
            </div>
          </motion.div>

          {/* Restaurant Images */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mt-16"
          >
            <h2 className="text-3xl font-bold text-gradient text-center mb-8">Visit Our Location</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <img
                src="https://i.ibb.co/VWzv99wv/Shop-Outside.jpg"
                alt="Zalabya Exterior"
                className="rounded-lg w-full h-64 object-cover"
              />
              <img
                src="https://i.ibb.co/0y0WmmwB/Inside.jpg"
                alt="Zalabya Interior"
                className="rounded-lg w-full h-64 object-cover"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default Contact;